import Icon from '@/components/ui/AppIcon';

interface Ticket {
  id: string;
  title: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created: string;
  lastUpdate: string;
  assignedTo: string;
}

export default function RecentTickets() {
  const tickets: Ticket[] = [
    {
      id: "TKT-2026-0142",
      title: "Email server intermittent connectivity issues",
      status: "in-progress",
      priority: "high",
      created: "Jan 24, 2026",
      lastUpdate: "2 hours ago",
      assignedTo: "Michael Chen"
    },
    {
      id: "TKT-2026-0138",
      title: "Request for additional cloud storage allocation",
      status: "open",
      priority: "medium",
      created: "Jan 23, 2026",
      lastUpdate: "5 hours ago",
      assignedTo: "Sarah Johnson"
    },
    {
      id: "TKT-2026-0135",
      title: "VPN access configuration for new employee",
      status: "resolved",
      priority: "low",
      created: "Jan 22, 2026",
      lastUpdate: "1 day ago",
      assignedTo: "David Martinez"
    }
  ];

  const getStatusColor = (status: Ticket['status']) => {
    switch (status) {
      case 'open':
        return 'bg-brand-primary/10 text-brand-primary border-brand-primary/20';
      case 'in-progress':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'resolved':
        return 'bg-success/10 text-success border-success/20';
      case 'closed':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority: Ticket['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'text-error';
      case 'high':
        return 'text-warning';
      case 'medium':
        return 'text-brand-secondary';
      case 'low':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusText = (status: Ticket['status']) => {
    switch (status) {
      case 'open':
        return 'Open';
      case 'in-progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="px-6 py-4 border-b border-border bg-muted/30 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-headline font-bold text-primary">Recent Support Tickets</h2>
          <p className="text-sm text-muted-foreground mt-1">Track your support requests</p>
        </div>
        <button className="px-4 py-2 bg-brand-conversion hover:bg-brand-conversion/90 text-white rounded-lg font-cta font-semibold text-sm transition-colors duration-200 flex items-center gap-2">
          <Icon name="PlusIcon" size={16} variant="outline" />
          <span>New Ticket</span>
        </button>
      </div>
      
      <div className="divide-y divide-border">
        {tickets.map((ticket) => (
          <div key={ticket.id} className="p-6 hover:bg-muted/30 transition-colors duration-200">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-accent font-semibold text-muted-foreground">
                    {ticket.id}
                  </span>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${getStatusColor(ticket.status)}`}>
                    {getStatusText(ticket.status)}
                  </span>
                  <Icon 
                    name="ExclamationCircleIcon" 
                    size={16} 
                    variant="solid"
                    className={getPriorityColor(ticket.priority)}
                  />
                </div>
                <h3 className="text-base font-headline font-semibold text-primary mb-3">
                  {ticket.title}
                </h3>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="CalendarIcon" size={14} variant="outline" />
                    <span>Created: {ticket.created}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={14} variant="outline" />
                    <span>Updated: {ticket.lastUpdate}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="UserIcon" size={14} variant="outline" />
                    <span>Assigned: {ticket.assignedTo}</span>
                  </div>
                </div>
              </div>
              
              <button className="px-4 py-2 text-sm font-cta font-semibold text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors duration-200 flex items-center gap-2 self-start">
                <span>View Details</span>
                <Icon name="ArrowRightIcon" size={16} variant="outline" />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="px-6 py-4 border-t border-border bg-muted/30">
        <button className="text-sm font-cta font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors duration-200 flex items-center gap-2">
          <span>View All Tickets</span>
          <Icon name="ArrowRightIcon" size={16} variant="outline" />
        </button>
      </div>
    </div>
  );
}